/**
 * Provides mappings between URL slugs and display names for Swedish counties
 * Cities are now fully dynamic - no hardcoded mappings needed
 */

import { slugify } from "./slugify";

export interface LocationMapping {
  slug: string;
  displayName: string;
  type: 'city' | 'county';
}

// Swedish counties with -lan suffix to avoid conflicts with cities
export const countyMappings: LocationMapping[] = [
  { slug: 'stockholm-lan', displayName: 'Stockholms län', type: 'county' },
  { slug: 'vastra-gotalands-lan', displayName: 'Västra Götalands län', type: 'county' },
  { slug: 'skane-lan', displayName: 'Skåne län', type: 'county' },
  { slug: 'ostergotlands-lan', displayName: 'Östergötlands län', type: 'county' },
  { slug: 'jonkopings-lan', displayName: 'Jönköpings län', type: 'county' },
  { slug: 'kronobergs-lan', displayName: 'Kronobergs län', type: 'county' },
  { slug: 'kalmar-lan', displayName: 'Kalmar län', type: 'county' },
  { slug: 'gotlands-lan', displayName: 'Gotlands län', type: 'county' },
  { slug: 'blekinge-lan', displayName: 'Blekinge län', type: 'county' },
  { slug: 'hallands-lan', displayName: 'Hallands län', type: 'county' },
  { slug: 'varmlands-lan', displayName: 'Värmlands län', type: 'county' },
  { slug: 'orebro-lan', displayName: 'Örebro län', type: 'county' },
  { slug: 'vastmanlands-lan', displayName: 'Västmanlands län', type: 'county' },
  { slug: 'dalarnas-lan', displayName: 'Dalarnas län', type: 'county' },
  { slug: 'gavleborgs-lan', displayName: 'Gävleborgs län', type: 'county' },
  { slug: 'vasternorrlands-lan', displayName: 'Västernorrlands län', type: 'county' },
  { slug: 'jamtlands-lan', displayName: 'Jämtlands län', type: 'county' },
  { slug: 'vasterbottens-lan', displayName: 'Västerbottens län', type: 'county' },
  { slug: 'norrbottens-lan', displayName: 'Norrbottens län', type: 'county' },
  { slug: 'uppsala-lan', displayName: 'Uppsala län', type: 'county' },
  { slug: 'sodermanlands-lan', displayName: 'Södermanlands län', type: 'county' },
];

// Build a map for fast county slug lookups (cities are fully dynamic)
const slugToLocationMap = new Map<string, LocationMapping>();
countyMappings.forEach(mapping => {
  slugToLocationMap.set(mapping.slug, mapping);
});

/**
 * Get display name from URL slug
 */
export function getDisplayNameFromSlug(slug: string): string | null {
  return slugToLocationMap.get(slug.toLowerCase())?.displayName || null;
}

/**
 * Get URL slug from display name
 */
export function getSlugFromLocation(location: string): string | null {
  const normalized = location.toLowerCase().trim();
  
  // Try exact match first
  for (const mapping of slugToLocationMap.values()) {
    if (mapping.displayName.toLowerCase() === normalized) {
      return mapping.slug;
    }
  }
  
  // Try partial match
  for (const mapping of slugToLocationMap.values()) {
    if (mapping.displayName.toLowerCase().includes(normalized) || 
        normalized.includes(mapping.displayName.toLowerCase())) {
      return mapping.slug;
    }
  }
  
  return null;
}

/**
 * Check if a slug is a valid location
 */
export function isValidLocationSlug(slug: string): boolean {
  return slugToLocationMap.has(slug.toLowerCase());
}

/**
 * Generate URL-safe slug from Swedish city name
 * Uses the centralized slugify utility for consistency
 */
export function generateCitySlug(cityName: string): string {
  return slugify(cityName);
}

/**
 * Determine if a slug represents a city or county
 * Counties end with -lan, all other slugs are dynamic cities
 */
export function getLocationType(slug: string): 'city' | 'county' | null {
  // Check if it's a hardcoded county (ends with -lan)
  if (slug.endsWith('-lan')) {
    return 'county';
  }
  
  // All other slugs are dynamic cities
  return 'city';
}
